import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ScrollView, TouchableOpacity, View, ActivityIndicator } from 'react-native'
import { Ionicons } from '@expo/vector-icons';
import { DatePicker } from 'react-native-woodpicker';

import api from "../../../../utils/api"
import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import { Icon } from '../../../../components/Icon';
import { Illustration } from '../../../../components/Illustration';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigation } from '@react-navigation/native';
import dayjs from 'dayjs';
import pusher from '../../../../utils/pusher';
import useActiveAdminCommunity from '@/hooks/useActiveAdminCommunity';


const ignoredTypes = ["ignored"];

// QR sigue mockeado (no hay endpoint a nivel comunidad — pendiente backend, punto K).
const QR_SUMMARY_MOCK = {
  generated: 342,
  read: 338,
  rejected: 8,
  active: 4,
};

export default function AccessDashboard({ }) {
  const { communityId } = useActiveAdminCommunity();
  const navigation = useNavigation<any>();

  // Resumen de TAGS con datos reales (mismo endpoint que la pantalla de Tags).
  const { data: tags } = useQuery<any>({
    queryKey: ['community.tags', communityId],
    queryFn: () => api.get(`/communities/${communityId}/access/tags?with=units`).then(r => r.data),
    enabled: !!communityId,
  });
  const tagsSummary = useMemo(() => {
    const list = Array.isArray(tags) ? tags : [];
    return {
      active: list.filter((t: any) => t.active).length,
      assigned: list.filter((t: any) => t.unit).length,
      unassigned: list.filter((t: any) => !t.unit).length,
      deactivated: list.filter((t: any) => !t.active).length,
    };
  }, [tags]);

  // Filtros existentes
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [eventType, setEventType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Filtros del nuevo diseño
  const [timeMode, setTimeMode] = useState<'today' | 'live'>('live'); // Hoy / En vivo
  const [sourceMode, setSourceMode] = useState<'tag' | 'qr' | 'all'>('all'); // Tag / QR / Todo

  // Paginación
  const [page, setPage] = useState(1);
  const [allEvents, setAllEvents] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);

  // Pusher
  const [pusherConnected, setPusherConnected] = useState(false);
  const [realtimeEvents, setRealtimeEvents] = useState<any[]>([]);
  const [newEventIds, setNewEventIds] = useState<Set<string>>(new Set());

  // Query params
  const queryParams = useMemo(() => {
    const params: any = { page };
    if (fromDate) params.from_date = dayjs(fromDate).format('YYYY-MM-DD');
    if (toDate) params.to_date = dayjs(toDate).format('YYYY-MM-DD');
    if (eventType !== 'all') params.event_type = eventType;
    if (sourceMode !== 'all') params.source = sourceMode;
    if (timeMode === 'today') params.from_date = dayjs().format('YYYY-MM-DD');
    return params;
  }, [page, fromDate, toDate, eventType, sourceMode, timeMode]);

  const { isLoading, data, isFetching } = useQuery<any>({
    queryKey: ['access-control.activity', communityId, queryParams],
    queryFn: () => api.get(`/communities/${communityId}/access/events`, {
      params: queryParams
    }).then(response => response.data),
    enabled: !!communityId,
    placeholderData: keepPreviousData,
  });

  // Clave que cambia cuando cambian filtros (no la página).
  // Permite distinguir entre "cambió el filtro → resetear" y "siguiente página → acumular".
  const filterKey = useMemo(
    () => JSON.stringify({ fromDate, toDate, eventType, timeMode, sourceMode }),
    [fromDate, toDate, eventType, timeMode, sourceMode]
  );
  const lastFilterKey = useRef(filterKey);

  // Reset de página al cambiar filtros.
  useEffect(() => {
    setPage(1);
    setRealtimeEvents([]);
  }, [filterKey]);

  // Sincroniza allEvents con la respuesta. Si los filtros cambiaron, reemplaza;
  // si es la misma vista pero página > 1, acumula.
  useEffect(() => {
    if (!data?.data) return;
    const filterChanged = lastFilterKey.current !== filterKey;
    lastFilterKey.current = filterKey;

    if (filterChanged || page === 1) {
      setAllEvents(data.data);
    } else {
      setAllEvents(prev => [...prev, ...data.data]);
    }
    setHasMore(!!(data.links?.next ?? data.next_page_url));
  }, [data, page, filterKey]);

  // Pusher setup
  useEffect(() => {
    if (!communityId) return;

    setPusherConnected(false);
    const channelName = `private-access.${communityId}.access-events`;
    const channel = pusher.subscribe(channelName);

    channel.bind('App\\Events\\Access\\AccessActivityCreated', function (data: any) {
      const activity = data.activity;
      if (!activity) return;
      if (ignoredTypes.indexOf(activity.type) !== -1) return;

      const eventDate = dayjs(activity.time);
      if (fromDate && eventDate.isBefore(dayjs(fromDate).startOf('day'))) return;
      if (toDate && eventDate.isAfter(dayjs(toDate).endOf('day'))) return;
      if (eventType !== 'all' && activity.type !== eventType) return;
      if (sourceMode !== 'all' && activity.source !== sourceMode) return;

      setRealtimeEvents(prev => {
        if (prev.find(e => e.uuid === activity.uuid)) return prev;
        return [activity, ...prev].slice(0, 10);
      });

      setNewEventIds(prev => new Set([...prev, activity.uuid]));
      setTimeout(() => {
        setNewEventIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(activity.uuid);
          return newSet;
        });
      }, 5000);
    });

    channel.bind('pusher:subscription_succeeded', function () {
      setPusherConnected(true);
    });

    channel.bind('pusher:subscription_error', function () {
      setPusherConnected(false);
    });

    return () => {
      channel.unbind_all();
      pusher.unsubscribe(channelName);
    };
  }, [communityId, fromDate, toDate, eventType, sourceMode]);

  // Combinar eventos en tiempo real con históricos
  const combinedEvents = useMemo(() => {
    const eventMap = new Map();
    realtimeEvents.forEach(event => eventMap.set(event.uuid, event));
    allEvents.forEach(event => {
      if (!eventMap.has(event.uuid)) eventMap.set(event.uuid, event);
    });

    return Array.from(eventMap.values()).sort((a, b) => {
      return dayjs(b.time || b.created_at).diff(dayjs(a.time || a.created_at));
    });
  }, [realtimeEvents, allEvents]);

  // Contadores de actividad derivados de los eventos cargados.
  const activityCounters = useMemo(() => {
    const counters = {
      tag: { in: 0, out: 0 },
      qr: { in: 0, out: 0 },
    };
    combinedEvents.forEach((e: any) => {
      const isEntry = ['entry', 'scan_entry', 'manual_entry'].includes(e.type);
      const isExit = e.type === 'exit';
      if (e.source === 'tag') {
        if (isEntry) counters.tag.in++;
        else if (isExit) counters.tag.out++;
      } else if (e.source === 'qr') {
        if (isEntry) counters.qr.in++;
        else if (isExit) counters.qr.out++;
      }
    });
    return counters;
  }, [combinedEvents]);

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore && !isLoading) {
      setPage(prev => prev + 1);
    }
  }, [isFetching, hasMore, isLoading]);

  const clearFilters = useCallback(() => {
    setFromDate(null);
    setToDate(null);
    setEventType('all');
  }, []);

  const isExit = (type: string) => type?.toLowerCase() === 'exit';
  const isDenied = (type: string) => ['denied', 'card_unregistered', 'card_expired'].includes(type?.toLowerCase());

  const getEventTypeLabel = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'entry': return 'Entrada';
      case 'exit': return 'Salida';
      case 'denied': return 'Denegado';
      case 'card_unregistered': return 'No registrada';
      case 'card_expired': return 'Expirada';
      case 'door_opened': return 'Puerta abierta';
      case 'door_button': return 'Botón salida';
      case 'system_reboot': return 'Reinicio';
      case 'system_start': return 'Inicio sistema';
      case 'remote_open': return 'Apertura remota';
      default: return type;
    }
  };

  const totalShown = combinedEvents.length;
  const totalAvailable = data?.total ?? combinedEvents.length;

  return (
    <View style={tw``}>
      {/* ===== Fila superior: tarjetas de resumen ===== */}
      <View style={tw`flex-row gap-4 mb-4`}>
        {/* Tarjeta TAGS */}
        <SummaryCard
          iconNode={<Illustration name="tags" width={26} height={26} />}
          title="Resumen de TAGS"
          actionLabel="Administrar TAGS ›"
          onActionPress={() => navigation.navigate('Admin.AccessControl.Index')}
          borderColor="border-primary"
        >
          <View style={tw`flex-row`}>
            <View style={tw`flex-1 pr-4`}>
              <SummaryStat label="Tags activos" value={tagsSummary.active} />
              <SummaryStat label="Tags asignados" value={tagsSummary.assigned} />
            </View>
            <View style={tw`flex-1 pl-4 border-l border-gray-100`}>
              <SummaryStat label="Tags sin asignar" value={tagsSummary.unassigned} />
              <SummaryStat label="Tags desactivados" value={tagsSummary.deactivated} />
            </View>
          </View>
        </SummaryCard>

        {/* Tarjeta QR */}
        <SummaryCard
          iconNode={<Icon name="qr" variant="v2" size={24} />}
          title="Resumen de QR para invitados"
          borderColor="border-primary"
        >
          <View style={tw`flex-row`}>
            <View style={tw`flex-1 pr-4`}>
              <SummaryStat
                label="Generados"
                value={QR_SUMMARY_MOCK.generated}
                iconName="qrGenerados"
                valueLeft
              />
              <SummaryStat
                label="Leídos"
                value={QR_SUMMARY_MOCK.read}
                iconName="qrLeidos"
                valueLeft
              />
            </View>
            <View style={tw`flex-1 pl-4 border-l border-gray-100`}>
              <SummaryStat
                label="Rechazados"
                value={QR_SUMMARY_MOCK.rejected}
                iconName="qrRechazado"
                valueLeft
              />
              <SummaryStat
                label="Activos"
                value={QR_SUMMARY_MOCK.active}
                iconName="qrActivo"
                valueLeft
              />
            </View>
          </View>
        </SummaryCard>
      </View>

      {/* ===== Card grande: actividad ===== */}
      <View style={tw`flex-1 bg-white rounded-2xl border border-gray-200 overflow-hidden`}>
        {/* Header con tabs y contadores */}
        <View style={tw`flex-row items-center justify-between px-5 py-4 border-b border-gray-100`}>
          <View style={tw`flex-row items-center`}>
            <Text style={tw`text-lg font-600 text-primary mr-8`}>Actividad</Text>

            {/* Contadores Tag */}
            <View style={tw`flex-row items-center mr-6`}>
              <Ionicons name="radio" size={16} color={tw.color('secondary')} />
              <View style={tw`flex-row items-center ml-3`}>
                <Ionicons name="arrow-down" size={14} color={tw.color('primary')} />
                <Text style={tw`ml-1 text-sm text-gray-700`}>{activityCounters.tag.in}</Text>
              </View>
              <View style={tw`flex-row items-center ml-3`}>
                <Ionicons name="arrow-up" size={14} color={tw.color('red')} />
                <Text style={tw`ml-1 text-sm text-gray-700`}>{activityCounters.tag.out}</Text>
              </View>
            </View>

            {/* Contadores QR */}
            <View style={tw`flex-row items-center`}>
              <Ionicons name="qr-code-outline" size={16} color={tw.color('primary')} />
              <View style={tw`flex-row items-center ml-3`}>
                <Ionicons name="arrow-down" size={14} color={tw.color('primary')} />
                <Text style={tw`ml-1 text-sm text-gray-700`}>{activityCounters.qr.in}</Text>
              </View>
              <View style={tw`flex-row items-center ml-3`}>
                <Ionicons name="arrow-up" size={14} color={tw.color('red')} />
                <Text style={tw`ml-1 text-sm text-gray-700`}>{activityCounters.qr.out}</Text>
              </View>
            </View>
          </View>

          <View style={tw`flex-row items-center`}>
            {/* Toggle Hoy / En vivo */}
            {/* <SegmentedControl
              value={timeMode}
              onChange={setTimeMode}
              options={[
                { value: 'today', label: 'Hoy' },
                { value: 'live', label: 'En vivo' },
              ]}
              activeColor="bg-cyan-100"
              activeTextColor="text-cyan-700"
            /> */}

            <View style={tw`w-3`} />

            {/* Toggle Tag / QR / Todo */}
            <SegmentedControl
              value={sourceMode}
              onChange={setSourceMode}
              options={[
                { value: 'tag', label: 'Tag' },
                { value: 'qr', label: 'QR' },
                { value: 'all', label: 'Todo' },
              ]}
              activeColor="bg-primary"
              activeTextColor="text-white"
            />

            <TouchableOpacity
              onPress={() => setShowFilters(!showFilters)}
              style={tw`ml-3 w-9 h-9 rounded-lg bg-gray-100 border border-gray-200 items-center justify-center`}
            >
              <Ionicons name="filter" size={18} color="#6b7280" />
              {(fromDate || toDate || eventType !== 'all') && (
                <View style={tw`absolute top-1 right-1 bg-primary w-2 h-2 rounded-full`} />
              )}
            </TouchableOpacity>

            {/* Indicador de estado pusher */}
            <View style={tw`flex-row items-center ml-3`}>
              <View style={tw.style(`w-2 h-2 rounded-full mr-2`, pusherConnected ? 'bg-green-500' : 'bg-gray-400')} />
              <Text style={tw`text-xs text-gray-500`}>
                {pusherConnected ? 'En vivo' : 'Conectando…'}
              </Text>
            </View>
          </View>
        </View>

        {/* Panel de filtros existente, colapsable */}
        {showFilters && (
          <View style={tw`px-5 py-4 bg-gray-50 border-b border-gray-100`}>
            <View style={tw`flex-row gap-4`}>
              <View style={tw`flex-1`}>
                <Text style={tw`text-xs text-gray-600 mb-2`}>Desde</Text>
                <DatePicker
                  value={fromDate}
                  onDateChange={setFromDate}
                  title="Seleccionar fecha"
                  text={fromDate ? dayjs(fromDate).format('D MMM YYYY') : 'Seleccionar...'}
                  style={tw`bg-white border border-gray-200 px-4 h-11 rounded-lg`}
                  isNullable={true}
                  locale="es"
                  androidDisplay="calendar"
                  textColor="#000"
                />
              </View>

              <View style={tw`flex-1`}>
                <Text style={tw`text-xs text-gray-600 mb-2`}>Hasta</Text>
                <DatePicker
                  value={toDate}
                  onDateChange={setToDate}
                  title="Seleccionar fecha"
                  text={toDate ? dayjs(toDate).format('D MMM YYYY') : 'Seleccionar...'}
                  style={tw`bg-white border border-gray-200 px-4 h-11 rounded-lg`}
                  isNullable={true}
                  locale="es"
                  androidDisplay="calendar"
                  textColor="#000"
                />
              </View>

              <View style={tw`flex-2`}>
                <Text style={tw`text-xs text-gray-600 mb-2`}>Tipo de evento</Text>
                <View style={tw`flex-row flex-wrap`}>
                  {[
                    { value: 'all', label: 'Todos' },
                    { value: 'entry', label: 'Entrada' },
                    { value: 'exit', label: 'Salida' },
                    { value: 'denied', label: 'Denegado' },
                  ].map(option => (
                    <TouchableOpacity
                      key={option.value}
                      onPress={() => setEventType(option.value)}
                      style={tw.style(
                        `px-4 py-2 rounded-full mr-2 mb-2`,
                        eventType === option.value ? 'bg-primary' : 'bg-white border border-gray-200'
                      )}
                    >
                      <Text style={tw.style(
                        `text-sm font-500`,
                        eventType === option.value ? 'text-white' : 'text-gray-700'
                      )}>
                        {option.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </View>

            {(fromDate || toDate || eventType !== 'all') && (
              <TouchableOpacity onPress={clearFilters} style={tw`mt-2 self-start`}>
                <Text style={tw`text-primary font-500 text-sm`}>Limpiar filtros</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Header de tabla */}
        <View style={tw`flex-row px-5 py-3 border-b border-gray-200/70 bg-gray-50`}>
          <Text style={tw`w-20 text-xs font-500 text-gray-500`}>Hora</Text>
          <Text style={tw`w-36 text-xs font-500 text-gray-500`}>Acceso</Text>
          <Text style={tw`flex-1 text-xs font-500 text-gray-500`}>Descripción</Text>
          <Text style={tw`flex-1 text-xs font-500 text-gray-500`}>Dirección / residente</Text>
          <Text style={tw`w-28 text-xs font-500 text-gray-500`}>Lectura</Text>
          <Text style={tw`w-24 text-xs font-500 text-gray-500`}>Estatus</Text>
          <Text style={tw`w-24 text-xs font-500 text-gray-500`}>Acción</Text>
          <Text style={tw`w-24 text-xs font-500 text-gray-500`}>Evidencia</Text>
        </View>

        {/* Filas */}
        {isLoading && page === 1 && combinedEvents.length === 0 ? (
          <View style={tw`px-5 py-8 items-center`}>
            <ActivityIndicator size="small" color="#6366f1" />
            <Text style={tw`text-gray-400 mt-2`}>Cargando eventos...</Text>
          </View>
        ) : combinedEvents.length === 0 ? (
          <View style={tw`py-12 items-center`}>
            <Ionicons name="list-outline" size={48} color="#d1d5db" />
            <Text style={tw`text-gray-400 mt-3`}>No hay eventos registrados</Text>
          </View>
        ) : (
          <ScrollView style={tw`flex-1`} contentContainerStyle={tw`pb-4`}>
            {combinedEvents.map((entry) => (
              <ActivityRow
                key={entry.uuid}
                entry={entry}
                isNew={newEventIds.has(entry.uuid)}
                isExit={isExit(entry.type)}
                isDenied={isDenied(entry.type)}
                getEventTypeLabel={getEventTypeLabel}
              />
            ))}

            {hasMore && (
              <View style={tw`py-3 items-center`}>
                <TouchableOpacity
                  onPress={loadMore}
                  disabled={isFetching}
                  style={tw`px-4 py-2 rounded-full border border-gray-200`}
                >
                  {isFetching ? (
                    <ActivityIndicator size="small" color="#6366f1" />
                  ) : (
                    <Text style={tw`text-primary text-sm font-500`}>Cargar más</Text>
                  )}
                </TouchableOpacity>
              </View>
            )}
          </ScrollView>
        )}

        {/* Footer */}
        <View style={tw`px-5 py-3 border-t border-gray-200/70 items-center`}>
          <Text style={tw`text-sm text-gray-500`}>
            Mostrando {totalShown} de {totalAvailable}
          </Text>
        </View>
      </View>
    </View>
  )
}

// ============================================================
// Sub-componentes (en el mismo archivo a propósito, todo a la mano)
// ============================================================

function SummaryCard({ iconNode, title, actionLabel, onActionPress, borderColor, children }: any) {
  return (
    <View style={tw.style(`flex-1 bg-white rounded-2xl border p-5`, borderColor)}>
      <View style={tw`flex-row items-center justify-between mb-4`}>
        <View style={tw`flex-row items-center`}>
          {iconNode}
          <Text style={tw`text-base font-600 text-secondary ml-3`}>{title}</Text>
        </View>
        {actionLabel && onActionPress ? (
          <TouchableOpacity onPress={onActionPress}>
            <Text style={tw`text-secondary font-500 text-sm`}>{actionLabel}</Text>
          </TouchableOpacity>
        ) : null}
      </View>
      {children}
    </View>
  );
}

function SummaryStat({ label, value, iconName, valueLeft }: any) {
  if (valueLeft) {
    return (
      <View style={tw`flex-row items-center py-2`}>
        {iconName && <Icon name={iconName} size={18} style={tw`mr-2`} />}
        <Text style={tw`text-xl font-700 text-secondary mr-3`}>{value}</Text>
        <Text style={tw`text-sm text-gray-600`}>{label}</Text>
      </View>
    );
  }
  return (
    <View style={tw`flex-row items-center justify-between py-2`}>
      <Text style={tw`text-sm text-gray-600`}>{label}</Text>
      <Text style={tw`text-xl font-700 text-secondary`}>{value}</Text>
    </View>
  );
}

function SegmentedControl({ value, onChange, options }: any) {
  // Segmented sobre fondo blanco (track #F3F2F3, pill blanco activo, texto morado).
  return (
    <View style={tw`flex-row bg-[#F3F2F3] rounded-xl p-1 self-start`}>
      {options.map((opt: any) => {
        const isActive = value === opt.value;
        return (
          <TouchableOpacity
            key={opt.value}
            onPress={() => onChange(opt.value)}
            style={tw.style(`px-4 py-1.5 rounded-lg`, isActive && `bg-white shadow-sm`)}
          >
            <Text style={tw.style(`text-sm`, isActive ? `text-secondary font-600` : `text-gray-400 font-500`)}>
              {opt.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

function ActivityRow({ entry, isNew, isExit, isDenied, getEventTypeLabel }: any) {
  // Columnas "Lectura", "Estatus", "Acción", "Evidencia" en ActivityRow — 
  // derivadas heurísticamente de entry_pass/tag/denied 
  // para que se vean reales mientras decides de dónde sale cada dato.
  // Lo mismo con los contadores...
  const entryTime = dayjs(entry.time || entry.created_at);
  const source = entry.source;

  // "Lectura" según fuente del evento
  const lectura = source === 'qr' ? 'Tótem QR'
    : source === 'tag' ? 'Antena TAG'
    : source === 'manual' ? 'Manual'
    : '';

  // Estatus / Acción (parcialmente mockeado mientras no hay datos reales)
  const statusLabel = isDenied ? 'Inválido' : source === 'qr' ? 'Leído' : 'Activo';
  const statusColor = isDenied ? 'text-red' : 'text-primary';
  const actionLabel = isDenied ? 'No abrió' : 'Abrió';
  const actionColor = isDenied ? 'text-red' : 'text-primary';

  const descriptionMain = entry.entry_pass?.guest_name
    || entry.guest_name
    || entry.tag?.name
    || getEventTypeLabel(entry.type);
  const descriptionSub = entry.entry_pass?.guest_type_str || null;

  const unitName = entry.unit?.name
    || entry.entry_pass?.unit?.name
    || entry.tag?.unit?.name;
  const residentName = entry.tag?.resident?.name
    || entry.entry_pass?.unit?.resident?.name
    || null;

  return (
    <View
      style={tw.style(
        `flex-row items-center px-5 py-3 border-b border-gray-200/70`,
        isNew && 'bg-primary/5'
      )}
    >
      {/* Hora */}
      <View style={tw`w-20 flex-row items-center`}>
        <Ionicons
          name={isExit ? 'arrow-up' : 'arrow-down'}
          size={14}
          color={isExit ? tw.color('red') : tw.color('primary')}
        />
        <Text style={tw`ml-1.5 text-sm text-gray-900`}>{entryTime.format('H:mm')}</Text>
      </View>

      {/* Acceso */}
      <View style={tw`w-36`}>
        <Text style={tw`text-sm text-gray-900`} numberOfLines={1}>
          {entry.door?.name || (isExit ? 'Salida' : 'Entrada')}
        </Text>
      </View>

      {/* Descripción */}
      <View style={tw`flex-1 flex-row items-center pr-2`}>
        <Ionicons
          name={source === 'qr' ? 'qr-code-outline' : source === 'manual' ? 'create-outline' : 'radio'}
          size={16}
          color={source === 'qr' ? tw.color('primary') : source === 'manual' ? tw.color('gray-500') : tw.color('secondary')}
          style={tw`mr-2`}
        />
        <View style={tw`flex-1`}>
          {descriptionSub && (
            <Text style={tw`text-sm text-gray-900`} numberOfLines={1}>{descriptionSub}</Text>
          )}
          <Text style={tw`text-sm text-gray-600`} numberOfLines={1}>{descriptionMain}</Text>
        </View>
      </View>

      {/* Dirección / residente */}
      <View style={tw`flex-1 pr-2`}>
        {unitName && (
          <Text style={tw`text-sm text-gray-900 font-500`} numberOfLines={1}>{unitName}</Text>
        )}
        {residentName && (
          <Text style={tw`text-xs text-gray-600`} numberOfLines={1}>{residentName}</Text>
        )}
        {!unitName && !residentName && (
          <Text style={tw`text-sm text-gray-400`}>—</Text>
        )}
      </View>

      {/* Lectura */}
      <View style={tw`w-28`}>
        <Text style={tw`text-sm text-gray-700`} numberOfLines={1}>{lectura}</Text>
      </View>

      {/* Estatus */}
      <View style={tw`w-24`}>
        <Text style={tw.style(`text-sm font-500`, statusColor)}>{statusLabel}</Text>
      </View>

      {/* Acción */}
      <View style={tw`w-24`}>
        <Text style={tw.style(`text-sm font-500`, actionColor)}>{actionLabel}</Text>
      </View>

      {/* Evidencia */}
      <View style={tw`w-24`}>
        <View style={tw`w-9 h-9 bg-gray-200 rounded`} />
      </View>
    </View>
  );
}
